import React, { useCallback } from 'react';
import { MatchState, MatchAction } from '../../../../types';
import { TIMERS } from '../../../../constants';

interface MatchActionsHookReturn {
  // Actions principales
  handleSideSelect: (side: 'left' | 'right') => void;
  handleScoreClick: (player: 'A' | 'B') => void;
  handleRollback: () => void;
  
  // Actions de décision
  handleStrokeToA: () => void;
  handleStrokeToB: () => void;
  handleLet: (player: 'A' | 'B') => void;
  handleNoLet: (player: 'A' | 'B') => void;
  
  // Actions de modal Match Start
  handleMatchStartAction: (action: 'start' | 'break' | 'rollback') => void;
  
  // Actions de modal Game End
  handleGameEndAction: (action: 'next' | 'break' | 'rollback') => void;
  
  // Actions de modal Warning
  handleWarningAction: (player: 'A' | 'B' | null, action: string) => void;
  
  // Actions de conduct
  handleConductJustification: (justification: string) => void;

  handleWarningForPlayer: (player: 'A' | 'B') => void;
}

interface UseMatchActionsProps {
  matchState: MatchState;
  dispatch: React.Dispatch<MatchAction>;
  modalState: any; // Type à ajuster selon votre modalState
  onServerScores?: (serverScore: number, receiverScore: number) => void;
  onReceiverScores?: (newServerScore: number, newReceiverScore: number) => void;
}

export const useMatchActions = ({
  matchState,
  dispatch,
  modalState,
  onServerScores,
  onReceiverScores
}: UseMatchActionsProps): MatchActionsHookReturn => {
  
  // Actions principales
  const handleSideSelect = useCallback((side: 'left' | 'right') => {
    dispatch({ type: 'SET_SERVING_SIDE', side });
  }, [dispatch]);
  
  const handleScoreClick = useCallback((player: 'A' | 'B') => {
    // On peut scorer même sans côté sélectionné (sera assigné par défaut dans le reducer)
    if (player === matchState.servingPlayer) {
      // Le serveur marque : annonce du score simple
      if (onServerScores) {
        const newServerScore = matchState.gameScores[player] + 1;
        const receiverScore = matchState.gameScores[player === 'A' ? 'B' : 'A'];
        onServerScores(newServerScore, receiverScore);
      }
    } else {
      // Le receveur marque : hand out (changement de service)
      // Le receveur devient le nouveau serveur
      if (onReceiverScores) {
        const newServerScore = matchState.gameScores[player] + 1; // Score du nouveau serveur (ex-receveur)
        const newReceiverScore = matchState.gameScores[player === 'A' ? 'B' : 'A']; // Score du nouveau receveur (ex-serveur)
        onReceiverScores(newServerScore, newReceiverScore);
      }
    }
    dispatch({ type: 'SCORE_POINT', player });
  }, [matchState.servingPlayer, matchState.gameScores, dispatch, onServerScores, onReceiverScores]);
  
  const handleRollback = () => {
    dispatch({ type: 'ROLLBACK' });
  };
  
  // Actions de décision
  const handleStrokeToA = () => {
    dispatch({ type: 'STROKE_TO', player: 'A' });
  };
  
  const handleStrokeToB = () => {
    dispatch({ type: 'STROKE_TO', player: 'B' });
  };
  
  const handleLet = (player: 'A' | 'B') => {
    dispatch({ type: 'SET_DECISION', decision: 'LET', player });
  };

  const handleNoLet = (player: 'A' | 'B') => {
    dispatch({ type: 'SET_DECISION', decision: 'NOLET', player });
  };
  
  // Actions de modal Match Start
  const handleMatchStartAction = (action: 'start' | 'break' | 'rollback') => {
    modalState.closeMatchStart();
    
    switch (action) {
      case 'start':
        // Démarrer le match - rien à faire de plus
        break;
      case 'break':
        // Lancer timer de 90 secondes
        modalState.openTimerWithTimer(TIMERS.before_game);
        break;
      case 'rollback':
        // Retour à l'écran Before Match
        dispatch({ type: 'ROLLBACK_TO_BEFORE' });
        break;
    }
  };
  
  // Actions de modal Game End
  const handleGameEndAction = (action: 'next' | 'break' | 'rollback') => {
    // TOUJOURS fermer la modal en premier
    modalState.closeGameEnd();

    if (action === 'rollback') {
      // Pour rollback, petit délai pour s'assurer que le popup est fermé
      setTimeout(() => {
        dispatch({ type: 'ROLLBACK' });
      }, 100);
    } else {
      // Marquer qu'une annonce game_start est en attente
      modalState.setPendingGameStart(true);

      switch (action) {
        case 'next':
          // Jeu suivant - l'annonce sera déclenchée par le useEffect dans DuringMatch
          break;
        case 'break':
          // Lancer timer de 2 minutes - l'annonce sera déclenchée à la fermeture du timer
          modalState.openTimerWithTimer(TIMERS.between_games);
          break;
      }
    }
  };
  
  // Actions de modal Warning
  const handleWarningAction = (player: 'A' | 'B' | null, action: string) => {
    // Gérer les actions de décision (NOLET, LET, STROKE TO)
    // Le joueur passé est celui qui a fait la demande (cliqué sur Decision)
    if (action === 'nolet') {
      dispatch({ type: 'SET_DECISION', decision: 'NOLET', player: modalState.warningStep.selectedPlayer! });
      return;
    }
    if (action === 'let') {
      dispatch({ type: 'SET_DECISION', decision: 'LET', player: modalState.warningStep.selectedPlayer! });
      return;
    }
    if (action === 'stroke_to') {
      dispatch({ type: 'STROKE_TO', player: player! });
      return;
    }

    // Timer actions
    const timerActions: Record<string, { type: 'EQP' | 'INJ' | 'BLD' | 'SHR'; timerKey: keyof typeof TIMERS }> = {
      'timer_equipment': { type: 'EQP', timerKey: 'equipment' },
      'timer_injury': { type: 'INJ', timerKey: 'self_injury' },
      'timer_bleeding': { type: 'BLD', timerKey: 'self_bleeding' },
      'timer_shared': { type: 'SHR', timerKey: 'shared' },
    };

    if (timerActions[action]) {
      const { type, timerKey } = timerActions[action];
      const timer = TIMERS[timerKey];
      dispatch({
        type: 'TIMER_FOR_PLAYER',
        player: modalState.warningStep.selectedPlayer!,
        timerType: type,
        duration: timer.duration,
      });
      modalState.openTimerWithTimer(timer);
      modalState.closeWarning();
      return;
    }

    if (action === 'win_game_injury') {
      // Win game for injury - attribuer le jeu au joueur
      dispatch({
        type: 'WIN_GAME_INJURY',
        player: modalState.warningStep.selectedPlayer // Le joueur stocké dans warningStep
      });
      modalState.closeWarning();
    } else {
      // Pour les actions de conduct, ouvrir la popup de justification
      modalState.openConductJustification(
        modalState.warningStep.selectedPlayer,  // Le joueur stocké dans warningStep
        action,
        modalState.warningStep.selectedPlayerName // Le nom stocké dans warningStep
      );
      modalState.closeWarning();
    }
  };
  
  const handleWarningForPlayer = (player: 'A' | 'B') => {
    // Obtenir le nom du joueur
    const playerName = player === 'A' ? matchState.matchInfo.playerA : matchState.matchInfo.playerB;
    
    // Stocker le joueur et son nom dans warningStep (pour compatibilité avec le code existant)
    modalState.setWarningStep(2, player, playerName); // Étape 2 directement
    
    // Ouvrir le modal
    modalState.openWarning();
  };

  // Actions de conduct
  const handleConductJustification = (justification: string) => {
    // Appliquer l'action de conduct avec justification
    dispatch({ 
      type: 'CONDUCT_SANCTION', 
      sanctionType: modalState.conductJustificationModal.action,
      player: modalState.conductJustificationModal.player,
      justification: justification
    });
    modalState.closeConductJustification();
  };
  
  return {
    // Actions principales
    handleSideSelect,
    handleScoreClick,
    handleRollback,
    
    // Actions de décision
    handleStrokeToA,
    handleStrokeToB,
    handleLet,
    handleNoLet,
    
    // Actions de modal
    handleMatchStartAction,
    handleGameEndAction,
    handleWarningAction,
    handleConductJustification,

    handleWarningForPlayer
  };
};